import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const impersonateUser = async (userId) => {
  try {
    const res = await axios.post(`/api/admin/users/${userId}/impersonate`);
    window.location.href = res.data.redirect;
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to impersonate user.');
  }
};

const SPECIALTIES_OPTIONS = ['Hitting', 'Pitching', 'Fielding', 'Catching', 'Base Running', 'Mental Game'];
const STATUS_COLORS = { confirmed:'badge-success', pending:'badge-warning', cancelled:'badge-error', completed:'badge-info', no_show:'badge-default' };

const formatDate = d => {
  if (!d) return '—';
  const parts = d.split('T')[0].split('-');
  const date = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]));
  return date.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
};

const formatTime = t => {
  if (!t) return '—';
  const [h,m] = t.split(':');
  const hour = parseInt(h);
  return `${hour>12?hour-12:hour||12}:${m} ${hour>=12?'PM':'AM'}`;
};

function CreateAdminModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ first_name:'', last_name:'', email:'', phone:'', password:'' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault(); setLoading(true); setError('');
    try { await axios.post('/api/admin/users/admin', form); onCreated(); onClose(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to create admin.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><span className="modal-title">NEW ADMIN</span><button className="modal-close" onClick={onClose}>x</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="auth-error-banner"><span>!</span> {error}</div>}
            <div style={{ background:'rgba(250,204,21,0.08)', border:'1px solid rgba(250,204,21,0.2)', borderRadius:'var(--radius-md)', padding:'10px 14px' }}>
              <p style={{ fontSize:'0.8rem', color:'#facc15' }}>Admin accounts have full access to all areas of the system. Only create accounts for trusted staff.</p>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div className="form-group"><label className="form-label">First Name</label><input name="first_name" className="form-input" value={form.first_name} onChange={handleChange} required /></div>
              <div className="form-group"><label className="form-label">Last Name</label><input name="last_name" className="form-input" value={form.last_name} onChange={handleChange} required /></div>
            </div>
            <div className="form-group"><label className="form-label">Email</label><input name="email" type="email" className="form-input" value={form.email} onChange={handleChange} required /></div>
            <div className="form-group"><label className="form-label">Phone (optional)</label><input name="phone" type="tel" className="form-input" value={form.phone} onChange={handleChange} /></div>
            <div className="form-group"><label className="form-label">Password</label><input name="password" type="password" className="form-input" value={form.password} onChange={handleChange} required minLength={8} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>{loading ? 'Creating...' : 'Create Admin'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateInstructorModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ first_name:'', last_name:'', email:'', phone:'', bio:'', session_duration_minutes:60, specialties:[] });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const toggleSpecialty = s => setForm(f => ({ ...f, specialties: f.specialties.includes(s) ? f.specialties.filter(x => x !== s) : [...f.specialties, s] }));

  const handleSubmit = async e => {
    e.preventDefault(); setLoading(true); setError('');
    try { await axios.post('/api/admin/users/instructor', form); onCreated(); onClose(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to create instructor.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><span className="modal-title">NEW INSTRUCTOR</span><button className="modal-close" onClick={onClose}>✕</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="auth-error-banner"><span>⚠</span> {error}</div>}
            <div style={{ background:'rgba(96,165,250,0.08)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:'var(--radius-md)', padding:'10px 14px' }}>
              <p style={{ fontSize:'0.8rem', color:'#60a5fa', margin:0 }}>A temporary password will be auto-generated and emailed to the instructor along with login instructions.</p>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div className="form-group"><label className="form-label">First Name</label><input name="first_name" className="form-input" value={form.first_name} onChange={handleChange} required /></div>
              <div className="form-group"><label className="form-label">Last Name</label><input name="last_name" className="form-input" value={form.last_name} onChange={handleChange} required /></div>
            </div>
            <div className="form-group"><label className="form-label">Email</label><input name="email" type="email" className="form-input" value={form.email} onChange={handleChange} required /></div>
            <div className="form-group"><label className="form-label">Phone (optional)</label><input name="phone" type="tel" className="form-input" value={form.phone} onChange={handleChange} /></div>
            <div className="form-group">
              <label className="form-label">Session Duration</label>
              <select name="session_duration_minutes" className="form-input" value={form.session_duration_minutes} onChange={handleChange}>
                {[30,45,60,90,120].map(d => <option key={d} value={d}>{d} min</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Bio (optional)</label><textarea name="bio" className="form-input" rows={3} value={form.bio} onChange={handleChange} style={{ resize:'vertical' }} /></div>
            <div className="form-group">
              <label className="form-label">Specialties</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'4px' }}>
                {SPECIALTIES_OPTIONS.map(s => (
                  <button key={s} type="button" onClick={() => toggleSpecialty(s)} className={`badge ${form.specialties.includes(s) ? 'badge-info' : 'badge-default'}`} style={{ cursor:'pointer' }}>{s}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>{loading ? 'Creating...' : 'Create Instructor'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserDetailPanel({ userId, onClose, onToggleActive, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/admin/users/${userId}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div style={{ position:'fixed', top:0, right:0, width:'420px', height:'100vh', background:'var(--bg-secondary)', borderLeft:'1px solid var(--border)', zIndex:150, display:'flex', flexDirection:'column', boxShadow:'-8px 0 32px rgba(0,0,0,0.5)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 24px', borderBottom:'1px solid var(--border)' }}>
        <span style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', letterSpacing:'0.05em' }}>USER DETAIL</span>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'24px' }}>
        {loading ? (
          <div className="empty-state"><span className="empty-state-text">Loading...</span></div>
        ) : !data ? (
          <div className="empty-state"><span className="empty-state-text">Failed to load.</span></div>
        ) : (
          <>
            {/* Profile */}
            <div style={{ display:'flex', alignItems:'center', gap:'16px', marginBottom:'24px' }}>
              <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'var(--bg-elevated)', border:'1px solid var(--border-light)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'700', fontSize:'1.2rem', color:'var(--text-secondary)', flexShrink:0 }}>
                {data.user.first_name[0]}{data.user.last_name[0]}
              </div>
              <div>
                <div style={{ fontWeight:'600', fontSize:'1.1rem', color:'var(--text-primary)' }}>{data.user.first_name} {data.user.last_name}</div>
                <div style={{ display:'flex', gap:'8px', marginTop:'4px' }}>
                  <span className={`badge ${data.user.role === 'instructor' ? 'badge-info' : 'badge-default'}`}>{data.user.role}</span>
                  <span className={`badge ${data.user.is_active ? 'badge-success' : 'badge-error'}`}>{data.user.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>

            {/* Contact info */}
            <div className="card" style={{ marginBottom:'16px' }}>
              <div className="stat-card-label" style={{ marginBottom:'12px' }}>Contact</div>
              {[['Email', data.user.email], ['Phone', data.user.phone || '—'], ['Member Since', formatDate(data.user.created_at)]].map(([label, value]) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{label}</span>
                  <span style={{ fontSize:'0.85rem', color:'var(--text-primary)' }}>{value}</span>
                </div>
              ))}
            </div>

            {/* Instructor profile */}
            {data.user.role === 'instructor' && (
              <div className="card" style={{ marginBottom:'16px' }}>
                <div className="stat-card-label" style={{ marginBottom:'12px' }}>Instructor Profile</div>
                {[
                  ['Session Duration', `${data.user.session_duration_minutes} min`],
                  ['Accepting Bookings', data.user.is_accepting_bookings ? 'Yes' : 'No'],
                  ['Min Notice', `${data.user.min_notice_hours}h`],
                  ['Max Advance', `${data.user.max_advance_booking_days} days`],
                ].map(([label, value]) => (
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
                    <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{label}</span>
                    <span style={{ fontSize:'0.85rem', color:'var(--text-primary)' }}>{value}</span>
                  </div>
                ))}
                {data.user.bio && <p style={{ fontSize:'0.85rem', color:'var(--text-secondary)', marginTop:'10px', lineHeight:'1.5' }}>{data.user.bio}</p>}
                {data.user.specialties?.length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'10px' }}>
                    {data.user.specialties.map(s => <span key={s} className="badge badge-default">{s}</span>)}
                  </div>
                )}
              </div>
            )}

            {/* Players */}
            {data.players?.length > 0 && (
              <div className="card" style={{ marginBottom:'16px' }}>
                <div className="stat-card-label" style={{ marginBottom:'12px' }}>Players ({data.players.length})</div>
                {data.players.map(p => (
                  <div key={p.id} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
                    <span style={{ fontSize:'0.85rem', color:'var(--text-primary)' }}>{p.first_name} {p.last_name}</span>
                    <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{p.position || '—'}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Recent sessions */}
            {data.sessions?.length > 0 && (
              <div className="card" style={{ marginBottom:'16px' }}>
                <div className="stat-card-label" style={{ marginBottom:'12px' }}>Recent Sessions ({data.sessions.length})</div>
                {data.sessions.slice(0,8).map(s => (
                  <div key={s.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                    <div>
                      <div style={{ fontSize:'0.85rem', color:'var(--text-primary)' }}>{formatDate(s.session_date)}</div>
                      <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{formatTime(s.start_time)} · {data.user.role === 'parent' ? `${s.instructor_first} ${s.instructor_last}` : `${s.parent_first} ${s.parent_last}`}</div>
                    </div>
                    <span className={`badge ${STATUS_COLORS[s.status]||'badge-default'}`}>{s.status}</span>
                  </div>
                ))}
              </div>
            )}

            {data.user.role === 'parent' && (
              <button
                className="btn btn-sm"
                style={{ width:'100%', marginBottom:'8px', background:'rgba(251,146,60,0.15)', color:'#fb923c', border:'1px solid rgba(251,146,60,0.3)', fontWeight:'600' }}
                disabled={promoting}
                onClick={async () => {
                  setPromoting(true);
                  try {
                    await axios.patch(`/api/admin/users/${data.user.id}/promote-to-instructor`);
                    onRefresh();
                    onClose();
                  } catch (err) {
                    alert(err.response?.data?.error || 'Failed to promote user.');
                  } finally { setPromoting(false); }
                }}>
                {promoting ? 'Promoting...' : 'Promote to Instructor'}
              </button>
            )}
            {data.user.role !== 'admin' && data.user.is_active && (
              <button
                className="btn btn-sm"
                style={{ width:'100%', marginBottom:'8px', background:'rgba(124,58,237,0.15)', color:'#a78bfa', border:'1px solid rgba(124,58,237,0.3)', fontWeight:'600' }}
                onClick={() => impersonateUser(data.user.id)}>
                Login As This User
              </button>
            )}
            <button
              className={`btn btn-sm ${data.user.is_active ? 'btn-outline' : 'btn-primary'}`}
              style={{ width:'100%', ...(data.user.is_active ? { borderColor:'var(--error)', color:'var(--error)' } : {}) }}
              onClick={() => onToggleActive(data.user.id)}>
              {data.user.is_active ? 'Deactivate Account' : 'Activate Account'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await axios.get('/api/admin/users', { params });
      setUsers(res.data.users);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleActive = async id => {
    try { await axios.patch(`/api/admin/users/${id}/toggle-active`); fetchUsers(); setSelectedUser(null); }
    catch (err) { console.error(err); }
  };
  const [deleteConfirm, setDeleteConfirm] = useState(null); // holds user object to delete
  const [deleteLoading, setDeleteLoading] = useState(false);
  const deleteUser = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/api/admin/users/${deleteConfirm.id}`);
      fetchUsers();
      setDeleteConfirm(null);
      setSelectedUser(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user.');
    } finally { setDeleteLoading(false); }
  };

  const exportCSV = () => {
    const headers = ['Name','Email','Role','Phone','Sessions','Status','Member Since'];
    const rows = users.map(u => [
      `${u.first_name} ${u.last_name}`, u.email, u.role,
      u.phone || '', u.session_count,
      u.is_active ? 'Active' : 'Inactive',
      formatDate(u.created_at)
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'users.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <div className="page-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div><h1 className="page-title">USERS</h1><p className="page-subtitle">Manage instructors and parents</p></div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button className="btn btn-outline btn-sm" onClick={exportCSV}>↓ CSV</button>
          <button className="btn btn-outline btn-sm" onClick={() => setShowAdminModal(true)}>+ New Admin</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ New Instructor</button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-toolbar">
          <span className="table-toolbar-title">All Users</span>
          <input className="table-search" placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="table-search" style={{ width:'140px' }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
            <option value="">All Roles</option>
            <option value="instructor">Instructors</option>
            <option value="parent">Parents</option>
          </select>
        </div>
        {loading ? (
          <div className="empty-state"><span className="empty-state-text">Loading...</span></div>
        ) : users.length === 0 ? (
          <div className="empty-state"><span className="empty-state-icon">👥</span><span className="empty-state-text">No users found</span></div>
        ) : (
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ cursor:'pointer' }}>
                  <td className="td-primary" onClick={() => setSelectedUser(u.id)}>{u.first_name} {u.last_name}</td>
                  <td onClick={() => setSelectedUser(u.id)}>{u.email}</td>
                  <td><span className={`badge ${u.role === 'instructor' ? 'badge-info' : u.role === 'admin' ? 'badge-warning' : 'badge-default'}`}>{u.role}</span></td>
                  <td><span className={`badge ${u.is_active ? 'badge-success' : 'badge-error'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
                      <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.75rem', padding:'4px 8px' }} onClick={() => setSelectedUser(u.id)}>View</button>
                      {u.role === 'parent' && <button className="btn btn-sm" style={{ fontSize:'0.75rem', padding:'4px 8px', background:'rgba(251,146,60,0.15)', color:'#fb923c', border:'1px solid rgba(251,146,60,0.3)' }} onClick={async () => { try { await axios.patch(`/api/admin/users/${u.id}/promote-to-instructor`); fetchUsers(); } catch (err) { alert(err.response?.data?.error || 'Failed to promote user.'); } }}>Make Instructor</button>}
                      {u.role !== 'admin' && u.is_active && <button className="btn btn-sm" style={{ fontSize:'0.75rem', padding:'4px 8px', background:'rgba(124,58,237,0.15)', color:'#a78bfa', border:'1px solid rgba(124,58,237,0.3)' }} onClick={() => impersonateUser(u.id)}>Login As</button>}
                      <button className={`btn btn-sm ${u.is_active ? 'btn-outline' : 'btn-primary'}`} style={{ fontSize:'0.75rem', padding:'4px 8px' }} onClick={() => toggleActive(u.id)}>{u.is_active ? 'Deactivate' : 'Activate'}</button>
                      <button className="btn btn-sm" style={{ fontSize:'0.75rem', padding:'4px 8px', background:'rgba(248,113,113,0.15)', color:'#f87171', border:'1px solid rgba(248,113,113,0.3)' }} onClick={() => setDeleteConfirm(u)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && <CreateInstructorModal onClose={() => setShowModal(false)} onCreated={fetchUsers} />}
      {showAdminModal && <CreateAdminModal onClose={() => setShowAdminModal(false)} onCreated={fetchUsers} />}
      {selectedUser && <UserDetailPanel userId={selectedUser} onClose={() => setSelectedUser(null)} onToggleActive={toggleActive} onRefresh={fetchUsers} />}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteConfirm(null)}>
          <div className="modal" style={{ maxWidth:'440px' }}>
            {deleteConfirm.is_active ? (
              <>
                <div className="modal-header">
                  <span className="modal-title" style={{ color:'#facc15' }}>DEACTIVATE FIRST</span>
                  <button className="modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
                </div>
                <div className="modal-body">
                  <div style={{ textAlign:'center', padding:'8px 0' }}>
                    <div style={{ fontSize:'2.5rem', marginBottom:'16px' }}>🔒</div>
                    <p style={{ color:'var(--text-primary)', fontWeight:'600', fontSize:'1rem', marginBottom:'12px' }}>
                      Account must be deactivated first
                    </p>
                    <p style={{ color:'var(--text-secondary)', fontSize:'0.875rem', lineHeight:'1.6' }}>
                      <strong>{deleteConfirm.first_name} {deleteConfirm.last_name}'s</strong> account is currently active.
                      You must deactivate it before it can be permanently deleted.
                    </p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline btn-sm" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                  <button
                    className="btn btn-sm"
                    style={{ background:'rgba(250,204,21,0.15)', color:'#facc15', border:'1px solid rgba(250,204,21,0.3)', fontWeight:'700' }}
                    disabled={deleteLoading}
                    onClick={async () => {
                      setDeleteLoading(true);
                      try {
                        await axios.patch(`/api/admin/users/${deleteConfirm.id}/toggle-active`);
                        setDeleteConfirm(u => ({ ...u, is_active: false }));
                        fetchUsers();
                      } catch (err) { alert(err.response?.data?.error || 'Failed to deactivate.'); }
                      finally { setDeleteLoading(false); }
                    }}>
                    {deleteLoading ? 'Deactivating...' : 'Deactivate Account'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-header">
                  <span className="modal-title" style={{ color:'#f87171' }}>DELETE USER</span>
                  <button className="modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
                </div>
                <div className="modal-body">
                  <div style={{ textAlign:'center', padding:'8px 0' }}>
                    <div style={{ fontSize:'2.5rem', marginBottom:'16px' }}>⚠</div>
                    <p style={{ color:'var(--text-primary)', fontWeight:'600', fontSize:'1rem', marginBottom:'8px' }}>
                      Permanently delete {deleteConfirm.first_name} {deleteConfirm.last_name}?
                    </p>
                    <p style={{ color:'var(--text-secondary)', fontSize:'0.875rem', lineHeight:'1.6', marginBottom:'8px' }}>
                      This will permanently delete their account and ALL associated data including:
                    </p>
                    <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:'8px', padding:'12px 16px', marginBottom:'16px', textAlign:'left' }}>
                      <p style={{ color:'#f87171', fontSize:'0.8rem', margin:'2px 0' }}>• All sessions (past and upcoming)</p>
                      <p style={{ color:'#f87171', fontSize:'0.8rem', margin:'2px 0' }}>• All player profiles</p>
                      <p style={{ color:'#f87171', fontSize:'0.8rem', margin:'2px 0' }}>• Availability slots and blocked dates</p>
                      <p style={{ color:'#f87171', fontSize:'0.8rem', margin:'2px 0' }}>• Profile photo and all account data</p>
                    </div>
                    <p style={{ color:'#f87171', fontWeight:'700', fontSize:'0.875rem' }}>This action cannot be undone.</p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline btn-sm" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                  <button
                    className="btn btn-sm"
                    style={{ background:'#f87171', color:'#0a0a0a', fontWeight:'700' }}
                    onClick={deleteUser}
                    disabled={deleteLoading}>
                    {deleteLoading ? 'Deleting...' : 'Yes, Permanently Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
